import React, {
  Dispatch,
  HTMLAttributes,
  Key,
  MouseEvent,
  RefObject,
  SetStateAction,
} from 'react';
import clsx from 'clsx';
import {
  notEmptyString,
} from '@populateui/utils';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import ToolTip from 'core/components/Tooltip';
import type {
  ClaimAuthorizationOption,
} from 'views/EMR/components/Authorizations/utils';
import AuthorizationNotes from 'pages/Dashboard/pages/Billing/ClaimForm/AuthorizationSelector/components/AuthorizationNotes';
import AuthorizationOptionContent from 'pages/Dashboard/pages/Billing/ClaimForm/AuthorizationSelector/components/AuthorizationOptionContent';
import {
  getSelectorRowKey,
  selectorRowHighlightClasses,
} from 'pages/Dashboard/pages/Billing/ClaimForm/AuthorizationSelector/utils';

export type AuthorizationOptionRowNotes = {
  activeNotesOption: string | null;
  activeNotesRef: RefObject<HTMLDivElement | null>;
  activateNotes: (optionKey: string, hasNotes: boolean) => void;
  cancelHideNotes: () => void;
  scheduleHideNotes: (optionKey: string) => void;
  setActiveNotesOption: Dispatch<SetStateAction<string | null>>;
  onClose: () => void;
  onNotesEscape: () => void;
};

type AuthorizationOptionRowProps = {
  option: ClaimAuthorizationOption;
  listItemProps: HTMLAttributes<HTMLLIElement> & { key?: Key };
  onRowMouseDown: (event: MouseEvent) => void;
  notes: AuthorizationOptionRowNotes;
};

export default function AuthorizationOptionRow({
  option,
  listItemProps,
  onRowMouseDown,
  notes,
}: AuthorizationOptionRowProps) {
  const hasNotes = option.authorizations.some((authorization) => (
    notEmptyString(authorization.notes)
  ));
  const optionKey = option.authorizationNumber;

  return (
    <ToolTip
      key={getSelectorRowKey({ kind: 'authorization', option })}
      open={hasNotes && notes.activeNotesOption === optionKey}
      title={hasNotes ? (
        <div>
          <div
            ref={notes.activeNotesOption === optionKey ? notes.activeNotesRef : undefined}
            aria-label={`Notes for ${option.authorizationNumber}`}
            aria-multiline="true"
            aria-readonly="true"
            role="textbox"
            tabIndex={0}
            className="max-h-80 w-80 overflow-y-auto break-words px-3 py-3 text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            onMouseEnter={notes.cancelHideNotes}
            onMouseLeave={(event) => {
              if (!event.currentTarget.contains(document.activeElement)) {
                notes.scheduleHideNotes(optionKey);
              }
            }}
            onBlur={(event) => {
              if (!event.currentTarget.contains(
                event.relatedTarget instanceof Node ? event.relatedTarget : null,
              )) {
                notes.setActiveNotesOption((current) => (current === optionKey ? null : current));
                notes.onClose();
              }
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                notes.onNotesEscape();
              }
            }}
          >
            <div className="mb-1 font-semibold text-gray-600">Notes</div>
            <AuthorizationNotes option={option} />
          </div>
        </div>
      ) : DEFAULT_EMPTY_VALUE}
      hide={!hasNotes}
      placement="right-start"
      arrow={false}
      offset={[0, 12]}
      isInteractive
      describeChild
      popperClassName="authorization-note-popper"
      classes={{ tooltip: '!m-0 !max-w-none !rounded-md !border !border-gray-200 !p-0 !shadow-lg' }}
    >
      <li
        {...listItemProps}
        key={listItemProps.key}
        role="option"
        aria-selected={listItemProps['aria-selected'] ?? false}
        className={clsx(
          listItemProps.className,
          '!w-full !border-b !border-gray-100 !px-3 !py-2',
          selectorRowHighlightClasses,
        )}
        onMouseDown={onRowMouseDown}
        onMouseEnter={() => {
          notes.activateNotes(optionKey, hasNotes);
        }}
        onMouseLeave={(event) => {
          if (!notes.activeNotesRef.current?.contains(
            event.relatedTarget instanceof Node ? event.relatedTarget : null,
          )) {
            notes.scheduleHideNotes(optionKey);
          }
        }}
      >
        <AuthorizationOptionContent option={option} />
      </li>
    </ToolTip>
  );
}
